import { useContext, createContext, useCallback } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { useAxios } from './use-axios'

export const ACLContext = createContext(null)

export function useACL() {
  const ctx = useContext(ACLContext)

  if (!ctx) {
    throw new Error('useACL must be used within an ACLContext')
  }

  return ctx
}

export function ACLProvider({ children }) {
  const { axios } = useAxios()
  const { user } = useAuth()
  const {
    data: { acls, tenants },
  } = useSuspenseQuery({
    queryKey: ['settings', user.id],
    queryFn: async ({ signal }) => {
      const { data } = await axios.get('/settings', { signal })
      return data
    },
  })

  const canAccess = useCallback(
    ({ tenant, roles = ['USER'] }) => {
      return acls.some(({ sk, roles: aclRoles }) => {
        if (sk === '*') {
          // Grant access if ACL applies to all useTenantenants, regardless of roles
          return true
        }

        // Check if the ACL matches the useTenant and has at least one allowed role
        return sk === tenant && roles.some((role) => aclRoles.includes(role))
      })
    },
    [acls]
  )

  return (
    <ACLContext.Provider
      value={{
        canAccess,
        acls,
        tenants: canAccess('*', ['ADMIN']) ? [{ ...user, id: '*' }, ...tenants] : tenants,
      }}
    >
      {children}
    </ACLContext.Provider>
  )
}

export function CanAccess({ tenant, roles, children, fallback = <></> }) {
  const { canAccess } = useACL()

  // Check if the tenant is assigned a valid role in the ACLs
  if (canAccess({ tenant, roles })) {
    return children
  }

  return fallback
}
